import { DEFAULT_GALLERY_ITEMS_POS } from '@/shared/constants';

interface PositionKind {
  kind: string;

  /**
   * Return the position if it is valid taking into account the page ads count,
   * undefined otherwise.
   */
  computeValue: (pageAdsCount: number) => number | undefined;
}

class BeforePosition implements PositionKind {
  kind = 'BeforePosition' as const;
  constructor(
    public computeValue: (pageAdsCount: number) => number | undefined
  ) {}
}

class AfterPosition implements PositionKind {
  kind = 'AfterPosition' as const;
  constructor(
    public computeValue: (pageAdsCount: number) => number | undefined
  ) {}
}

export type Position = BeforePosition | AfterPosition;

const defaultComputeValue =
  (position: number): ((c: number) => number | undefined) =>
  (pageAdsCount) => {
    if (position >= 0 && position <= pageAdsCount) {
      return position;
    }
    return undefined;
  };

/**
 * Create a before position.
 */
export function before(position: number): Position {
  return new BeforePosition(defaultComputeValue(position));
}

/**
 * Create an after position.
 */
export function after(position: number): Position {
  return new AfterPosition(defaultComputeValue(position));
}

const afterLastAdComputeValue = (pageAdsCount: number): number => pageAdsCount;

/**
 * Create a position at the end of the listing.
 */
export function endOfListing(): Position {
  return new AfterPosition(afterLastAdComputeValue);
}

const afterGalleryComputeValue = (pageAdsCount: number): number =>
  pageAdsCount < DEFAULT_GALLERY_ITEMS_POS
    ? pageAdsCount
    : DEFAULT_GALLERY_ITEMS_POS;

/**
 * Create a position after the default gallery position, or after the
 * position of the last ad if the ads count is less then the default
 * gallery position.
 */
export function galleryPosition(): Position {
  return new AfterPosition(afterGalleryComputeValue);
}

/**
 * Create a position after the gallery and completing the row based on the column count.
 */
export function afterGalleryAndCompleteRowPosition(
  columnCount = 1,
  galleryCount = 0
): Position {
  if (columnCount > 1) {
    return new AfterPosition((pageAdsCount: number) => {
      // First get the position after the gallery
      const galleryPos = afterGalleryComputeValue(pageAdsCount);
      // Account for gallery items when calculating row completion
      const afterGalleryPos = galleryPos + galleryCount;
      // Calculate the remainder to see if we need to complete the row
      const remainder = afterGalleryPos % columnCount;

      // Calculate position excluding gallery items since they don't count as ads for the index check
      if (remainder === 0) {
        return galleryPos;
      }

      return galleryPos + (columnCount - remainder);
    });
  }

  return galleryPosition();
}

/**
 * Returns a position for an ad slot placed after the gallery, aligned to complete the first
 * row, then repeated every `frequency * columnCount` ads for the given `iteration`.
 *
 * Multi‑column rules:
 * 1. Determine the first regular ad position after the gallery, ensuring the initial row is visually filled.
 * 2. Compute the "ideal" starting position (after `frequency` rows: columnCount * frequency).
 * 3. If that ideal position would still lie inside (or before) the gallery block, shift the start
 *    to immediately after the gallery; otherwise use the ideal position (adjusting for gallery items
 *    which are not counted as ads).
 * 4. Advance subsequent iterations by `frequency * columnCount`.
 *
 * Single column: falls back to a simple repeated `after(iteration * frequency)`.
 */
export function afterGalleryAndCompleteRowPositionRepeatedly({
  columnCount = 1,
  galleryCount = 0,
  iteration = 1,
  frequency = 1,
}): Position {
  if (columnCount > 1) {
    return new AfterPosition((pageAdsCount: number) => {
      // 1. Base position: first ad slot after the gallery once the initial row is completed.
      const basePos =
        afterGalleryAndCompleteRowPosition(
          columnCount,
          galleryCount
        ).computeValue(pageAdsCount) ?? 0;

      // 2. Ideal start if there were no gallery (after `frequency` full rows of ads).
      const idealStart = columnCount * frequency;

      // 3. Extent including gallery items so we know if the ideal position overlaps the gallery.
      const galleryExtent = basePos + galleryCount;

      // 4. Choose start: if gallery pushes beyond the ideal start, begin right after it; otherwise
      // use the ideal start (subtract galleryCount since gallery items are not ads and don't shift ad indices).
      const start =
        galleryExtent > idealStart ? basePos : idealStart - galleryCount;

      // 5. Offset by iteration (1-indexed): each iteration jumps `frequency * columnCount` ad slots.
      return start + (iteration - 1) * frequency * columnCount;
    });
  }

  // Single column: straightforward repeating after-position.
  return after(iteration * frequency);
}
